import {
  Badge,
  Card,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Search } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api/client";
import { formatDateTime } from "@/lib/format";

interface AdminAuditRow {
  id: string;
  created_at: string;
  clinic_id: string;
  clinic_name: string;
  actor_role: string | null;
  permission: string;
  verb: string;
  target_type: string | null;
  diff_keys: string[] | null;
  ip_address: string | null;
}

export function AdminAuditPage() {
  const [search, setSearch] = useState("");
  const [debSearch] = useDebouncedValue(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-all-audit"],
    queryFn: async () => {
      const { data } = await api.get<AdminAuditRow[]>("/admin/audit");
      return data;
    },
  });

  const filtered = (data ?? []).filter((a) => {
    if (!debSearch.trim()) return true;
    const q = debSearch.toLowerCase();
    return (
      a.permission.toLowerCase().includes(q) ||
      a.clinic_name.toLowerCase().includes(q) ||
      (a.actor_role ?? "").toLowerCase().includes(q) ||
      (a.target_type ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <Stack gap="lg">
      <PageHeader title="Audit jurnali (barcha klinikalar)" subtitle={`${filtered.length} yozuv`} />

      <Card padding="md">
        <TextInput
          rightSection={<Search size={18} strokeWidth={2} />}
          placeholder="Klinika, harakat, tur..."
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </Card>

      <Card padding={0} radius="xl">
        <Table.ScrollContainer minWidth={1100}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Vaqt</Table.Th>
                <Table.Th>Klinika</Table.Th>
                <Table.Th>Foydalanuvchi roli</Table.Th>
                <Table.Th>Harakat</Table.Th>
                <Table.Th>Tur</Table.Th>
                <Table.Th>Obyekt</Table.Th>
                <Table.Th>O'zgarishlar</Table.Th>
                <Table.Th>IP</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr><Table.Td colSpan={8}><Loader size="sm" /></Table.Td></Table.Tr>
              )}
              {filtered.map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td><Text size="xs" c="dimmed">{formatDateTime(a.created_at)}</Text></Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="brand" radius="sm">{a.clinic_name}</Badge>
                  </Table.Td>
                  <Table.Td><Text size="xs">{a.actor_role ?? "—"}</Text></Table.Td>
                  <Table.Td><Text size="xs" ff="monospace">{a.permission}</Text></Table.Td>
                  <Table.Td>
                    <Badge variant="light" size="xs" radius="sm">{a.verb}</Badge>
                  </Table.Td>
                  <Table.Td><Text size="xs">{a.target_type ?? "—"}</Text></Table.Td>
                  <Table.Td>
                    {a.diff_keys && a.diff_keys.length > 0 ? (
                      <Group gap={4}>
                        {a.diff_keys.slice(0, 2).map((k) => (
                          <Badge key={k} size="xs" variant="light" radius="sm">{k}</Badge>
                        ))}
                        {a.diff_keys.length > 2 && (
                          <Text size="xs" c="dimmed">+{a.diff_keys.length - 2}</Text>
                        )}
                      </Group>
                    ) : "—"}
                  </Table.Td>
                  <Table.Td><Text size="xs" ff="monospace" c="dimmed">{a.ip_address ?? "—"}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
